import React, { useEffect, useRef, useState } from 'react';
import { ICON } from '../icons/iconIds';
import SelkirkIcon from './icons/SelkirkIcon';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatMapHoleLabel } from '../utils/mapHoleLabel';
import { REPORT_PROGRESS_MAP } from '../utils/dailyReport/templateConfig';
import './DrillHoleMap.css';

const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ZONE_FILTERS = EMPTY_OBJECT;
const EMPTY_RIG_FILTERS = EMPTY_OBJECT;
const EMPTY_COLOR_MAP = EMPTY_OBJECT;
const EMPTY_RING_VISIBILITY = EMPTY_OBJECT;

/** Leaflet defaults to zoomSnap: 1 (whole levels). Use 0.25 for smoother wheel zoom. */
const SMOOTH_ZOOM_SNAP = 0.25;

// Fix Leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/** Outer ring = drilled … inner ring = analyzed (fixed hues; independent % per ring). */
export const PROGRESS_RING_METRICS = [
  { field: 'drilled_pct', label: 'Drilled', color: '#dc2626' },
  { field: 'logged_pct', label: 'Logged', color: '#2563eb' },
  { field: 'geoteched_pct', label: 'Geotech', color: '#ca8a04' },
  { field: 'sampled_pct', label: 'Sampled', color: '#059669' },
  { field: 'analysis_pct', label: 'Analyzed', color: '#7c3aed' },
];

const PROGRESS_MARKER_BASE_SIZE = 72;
const HOVER_MARKER_SCALE = 1.16;
const SELECTED_MARKER_SCALE_EXTRA = 1.07;
const CLUSTER_FAN_RADIUS_DEFAULT = 0.0001;
const CLUSTER_FAN_RADIUS_PROGRESS = 0.0002;

/** Centerline spacing must exceed stroke this much so colors do not fuse into fewer visible bands. */
const MIN_RING_CENTERLINE_GAP_FACTOR = 1.22;
const CLASSIC_MARKER_ENVELOPE = 56;
const CLASSIC_HOVER_SCALE = 1.12;
const EXPORT_MARKER_SIZE_IN_PROGRESS = 22;
const EXPORT_MARKER_SIZE_DEFAULT = 14;
const PROGRESS_MARKER_ENVELOPE = Math.ceil(
  PROGRESS_MARKER_BASE_SIZE * HOVER_MARKER_SCALE * SELECTED_MARKER_SCALE_EXTRA + 54
);

/** Must match hub div in createProgressIcon (rings stay outside opaque center dot). */
function progressInnerHubPx(renderPx) {
  return Math.max(18, Math.round(renderPx * 0.415));
}

function parseHoleProgressPcts(hole) {
  return PROGRESS_RING_METRICS.map(({ field }) => {
    const v = parseFloat(hole[field]);
    const pct = Number.isFinite(v) ? v : 0;
    return Math.min(100, Math.max(0, pct));
  });
}

function escapeMarkerText(raw) {
  return String(raw)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function metersToLatDelta(meters) {
  return meters / 111320;
}

function metersToLonDelta(meters, lat) {
  const latRad = (lat * Math.PI) / 180;
  return meters / Math.max(111320 * Math.cos(latRad), 1);
}

/**
 * Frame on in-progress (or framing) holes with ground-metre padding, then expand to include all markers.
 */
function buildReportProgressMapBounds(framingHoles, allHoles, config = REPORT_PROGRESS_MAP) {
  const frame = (framingHoles?.length ? framingHoles : allHoles).filter(
    (hole) => hole.latitude && hole.longitude
  );
  const visible = (allHoles || frame).filter((hole) => hole.latitude && hole.longitude);
  if (!frame.length || !visible.length) return null;

  const frameLats = frame.map((hole) => hole.latitude);
  const frameLons = frame.map((hole) => hole.longitude);
  let minLat = Math.min(...frameLats);
  let maxLat = Math.max(...frameLats);
  let minLon = Math.min(...frameLons);
  let maxLon = Math.max(...frameLons);

  const centerLat = (minLat + maxLat) / 2;
  const latSpan = maxLat - minLat;
  const lonSpan = maxLon - minLon;
  const latPad = Math.max(
    latSpan * (config.bufferRatio ?? 0.18),
    metersToLatDelta(config.minMarginM ?? 350)
  );
  const lonPad = Math.max(
    lonSpan * (config.bufferRatio ?? 0.18),
    metersToLonDelta(config.minMarginM ?? 350, centerLat)
  );

  minLat -= latPad;
  maxLat += latPad;
  minLon -= lonPad;
  maxLon += lonPad;

  visible.forEach((hole) => {
    minLat = Math.min(minLat, hole.latitude);
    maxLat = Math.max(maxLat, hole.latitude);
    minLon = Math.min(minLon, hole.longitude);
    maxLon = Math.max(maxLon, hole.longitude);
  });

  const edgeLat = metersToLatDelta(config.edgeMarginM ?? 60);
  const edgeLon = metersToLonDelta(config.edgeMarginM ?? 60, centerLat);

  return L.latLngBounds(
    [minLat - edgeLat, minLon - edgeLon],
    [maxLat + edgeLat, maxLon + edgeLon]
  );
}

/** Fixed square extent in meters centered on a collar (for hole-page inset maps). */
function buildFixedSpanBounds(lat, lon, spanM) {
  const half = spanM / 2;
  const latRad = (lat * Math.PI) / 180;
  const metersPerDegLon = 111320 * Math.cos(latRad);
  const latDelta = half / 111320;
  const lonDelta = half / Math.max(metersPerDegLon, 1);

  return L.latLngBounds(
    [lat - latDelta, lon - lonDelta],
    [lat + latDelta, lon + lonDelta]
  );
}

function fitMapToVisibleHoles(
  map,
  holes,
  exportMode,
  focusSpanM = null,
  focusCenter = null,
  exportBoundsHoles = null
) {
  if (!map || !holes?.length) return;

  if (exportMode && focusSpanM > 0) {
    const lat = Number(focusCenter?.lat ?? holes[0]?.latitude);
    const lon = Number(focusCenter?.lon ?? holes[0]?.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      map.fitBounds(buildFixedSpanBounds(lat, lon, focusSpanM), {
        padding: [0, 0],
        maxZoom: REPORT_PROGRESS_MAP.maxZoom ?? 18,
        animate: false,
      });
      return;
    }
  }

  if (exportMode) {
    const framingHoles =
      Array.isArray(exportBoundsHoles) && exportBoundsHoles.length > 0
        ? exportBoundsHoles
        : holes;
    const bounds = buildReportProgressMapBounds(framingHoles, holes);
    if (bounds) {
      const pad = REPORT_PROGRESS_MAP.fitPaddingPx ?? 24;
      map.fitBounds(bounds, {
        padding: [pad, pad],
        maxZoom: REPORT_PROGRESS_MAP.maxZoom ?? 15,
        animate: false,
      });
    }
    return;
  }

  const bounds = holes.map((hole) => [hole.latitude, hole.longitude]);
  map.fitBounds(bounds, { padding: [50, 50] });
}

function hasCustomMapViewport(viewport) {
  return (
    viewport &&
    Number.isFinite(Number(viewport.lat)) &&
    Number.isFinite(Number(viewport.lon)) &&
    Number.isFinite(Number(viewport.zoom))
  );
}

function applyMapViewForHoles(
  map,
  holes,
  {
    exportMode,
    focusSpanM,
    focusCenter,
    exportBoundsHoles,
    customViewport,
  },
  programmaticRef
) {
  if (programmaticRef) {
    programmaticRef.current = true;
    map.once('moveend', () => {
      programmaticRef.current = false;
    });
  }

  if (hasCustomMapViewport(customViewport)) {
    map.setView(
      [Number(customViewport.lat), Number(customViewport.lon)],
      Number(customViewport.zoom),
      { animate: false }
    );
    return;
  }

  fitMapToVisibleHoles(map, holes, exportMode, focusSpanM, focusCenter, exportBoundsHoles);
}

const WOLF_FAVICON_URL = `${process.env.PUBLIC_URL || ''}/favicon.ico`;

/** @param {Record<string, unknown>} hole */
function formatDrillRigLabel(hole) {
  const rig = hole.drill_rig != null ? String(hole.drill_rig).trim() : '';
  if (!rig) return '';
  if (/^drill\s*\d+/i.test(rig)) {
    const m = rig.match(/^drill\s*(\d+)/i);
    return m ? `Drill ${m[1]}` : rig;
  }
  const num = rig.match(/(\d{1,3})\s*$/);
  if (num) return `Drill ${num[1]}`;
  return rig;
}

/** White pill above marker — in-progress holes only. */
function inProgressRigLabelHtml(hole, bottomOffsetPx) {
  if (hole.hole_status !== 'In Progress') return '';
  const label = formatDrillRigLabel(hole);
  if (!label) return '';
  const safe = escapeMarkerText(label);
  return (
    `<div class="drill-ip-rig-label" style="bottom:calc(50% + ${bottomOffsetPx}px);">` +
    `<img class="drill-ip-rig-label__icon" src="${WOLF_FAVICON_URL}" alt="" width="14" height="14" decoding="async" />` +
    `<span class="drill-ip-rig-label__text">${safe}</span></div>`
  );
}

function ringVisible(specField, visibility) {
  if (visibility == null) return true;
  return visibility[specField] !== false;
}

function holeProgressTooltipContent(hole, visibility) {
  const pcts = parseHoleProgressPcts(hole);
  const lines = PROGRESS_RING_METRICS.map((spec, i) =>
    ringVisible(spec.field, visibility)
      ? `${spec.label}: ${pcts[i].toFixed(1)}%`
      : null
  ).filter(Boolean);
  const holeLine =
    hole.hole_id != null && String(hole.hole_id).trim() !== ''
      ? `<strong>${escapeMarkerText(String(hole.hole_id).trim())}</strong>`
      : '';
  const metricBlock = lines.length ? lines.join('<br/>') : 'All rings hidden — use legend to show metrics';
  if (holeLine) return `${holeLine}<br/>${metricBlock}`;
  return metricBlock;
}

/**
 * Returns padded SVG (+ extent) so thick strokes are not clipped; centers stay aligned with hub.
 * Constrain outer stroke to extent/2 - pad/2; separate ring centers enough to remain visually distinct.
 */
function buildProgressRingSvg(pcts, totalPx, visibility) {
  const edgePad = 4;
  const hubClearAboveDisc = 1.5;

  const visibleBands = PROGRESS_RING_METRICS.map((spec, i) =>
    ringVisible(spec.field, visibility) ? { spec, pct: pcts[i] } : null
  ).filter(Boolean);

  if (!visibleBands.length) {
    const emptySvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalPx}" height="${totalPx}" viewBox="0 0 ${totalPx} ${totalPx}" style="position:absolute;left:0;top:0;display:block;overflow:visible"></svg>`;
    return { svg: emptySvg, extent: totalPx };
  }

  const n = visibleBands.length;
  const hubPx = progressInnerHubPx(totalPx);
  const hubR = hubPx / 2;

  let stroke = Math.max(4.25, totalPx / 10.5);

  let extent = totalPx;
  let cx = totalPx / 2;
  let cy = totalPx / 2;
  let outerRingCenter = totalPx / 2;
  let innerRingCenter = hubR;
  let bleedPx = 0;

  for (let iter = 0; iter < 34; iter++) {
    bleedPx = Math.ceil(stroke / 2 + 5);
    extent = totalPx + 2 * bleedPx;
    cx = extent / 2;
    cy = extent / 2;
    outerRingCenter = extent / 2 - stroke / 2 - edgePad;
    innerRingCenter = hubR + stroke / 2 + hubClearAboveDisc;
    const span = outerRingCenter - innerRingCenter;

    if (outerRingCenter <= innerRingCenter) {
      stroke *= 0.88;
      continue;
    }
    if (n > 1 && span < (n - 1) * stroke * MIN_RING_CENTERLINE_GAP_FACTOR) {
      stroke *= 0.91;
      continue;
    }
    break;
  }

  stroke = Math.max(stroke, 2.2);
  bleedPx = Math.ceil(stroke / 2 + 5);
  extent = totalPx + 2 * bleedPx;
  cx = extent / 2;
  cy = extent / 2;
  outerRingCenter = extent / 2 - stroke / 2 - edgePad;
  innerRingCenter = hubR + stroke / 2 + hubClearAboveDisc;

  const stepInit =
    n > 1
      ? (outerRingCenter - innerRingCenter) / Math.max(n - 1, 1)
      : 0;

  let step = stepInit;
  let guard = 0;
  while (
    guard < 28 &&
    n > 1 &&
    stroke > 2.201 &&
    Number.isFinite(step) &&
    step < stroke * MIN_RING_CENTERLINE_GAP_FACTOR
  ) {
    guard++;
    stroke *= 0.9;
    stroke = Math.max(stroke, 2.2);
    bleedPx = Math.ceil(stroke / 2 + 5);
    extent = totalPx + 2 * bleedPx;
    cx = extent / 2;
    cy = extent / 2;
    outerRingCenter = extent / 2 - stroke / 2 - edgePad;
    innerRingCenter = hubR + stroke / 2 + hubClearAboveDisc;
    step =
      outerRingCenter > innerRingCenter
        ? (outerRingCenter - innerRingCenter) / Math.max(n - 1, 1)
        : 0;
  }

  const parts = [];

  visibleBands.forEach(({ spec, pct }, bandIdx) => {
    const r = outerRingCenter - bandIdx * step;
    const circum = 2 * Math.PI * r;
    const frac = Math.min(1, Math.max(0, pct / 100));
    const dashOn = frac * circum;
    const dashOff = Math.max(circum - dashOn, 0);

    parts.push(
      `<g transform="rotate(-90 ${cx} ${cy})">` +
        `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#64748b" stroke-opacity="0.88" stroke-width="${stroke}" stroke-linejoin="round" pointer-events="none" />`
    );

    if (dashOn > 0.001) {
      parts.push(
        `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${spec.color}" stroke-width="${stroke}" stroke-linecap="round" stroke-dasharray="${dashOn} ${dashOff}" stroke-linejoin="round" pointer-events="none" />`
      );
    }

    parts.push('</g>');
  });

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${extent}" height="${extent}" viewBox="0 0 ${extent} ${extent}" style="position:absolute;left:0;top:0;display:block;overflow:visible">${parts.join(
    ''
  )}</svg>`;

  return { svg, extent };
}

const DrillHoleMap = ({ 
  data, 
  onHoleClick, 
  selectedHoleId, 
  filterType = 'status',
  statusFilters, 
  zoneFilters = EMPTY_ZONE_FILTERS,
  rigFilters = EMPTY_RIG_FILTERS,
  zoneColorMap = EMPTY_COLOR_MAP,
  rigColorMap = EMPTY_COLOR_MAP,
  progressRingVisibility = EMPTY_RING_VISIBILITY,
  onStatusToggle,
  onZoneToggle,
  onRigToggle,
  onProgressMetricToggle,
  exportMode = false,
  showLegend = true,
  onExportReady,
  focusSpanM = null,
  focusCenter = null,
  highlightHoleId = null,
  exportBoundsHoles = null,
  exportViewport = null,
  initialViewport = null,
  onViewportChange = null,
  smoothZoom = false,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const [mapReady, setMapReady] = useState(false);
  const prevSelectedRef = useRef(null);
  const exportReadyFiredRef = useRef(false);
  const initialAutoFitDoneRef = useRef(false);
  const userControlledViewportRef = useRef(false);
  const programmaticViewChangeRef = useRef(false);
  
  // Invalidate map size when panel layout changes (selectedHoleId toggles panel visibility)
  useEffect(() => {
    if (!mapInstanceRef.current || !mapReady) return;
    
    // Check if selection state changed (panels opened/closed)
    const wasSelected = prevSelectedRef.current !== null;
    const isSelected = selectedHoleId !== null;
    
    if (wasSelected !== isSelected) {
      // Panel layout changed - invalidate map size after animation
      const timeoutId = setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize({ animate: true });
          
          // Re-fit bounds to visible markers
          const validHoles = data?.filter(hole => hole.latitude && hole.longitude) || [];
          if (validHoles.length > 0 && !onViewportChange && !userControlledViewportRef.current) {
            applyMapViewForHoles(
              mapInstanceRef.current,
              validHoles,
              {
                exportMode,
                focusSpanM,
                focusCenter,
                exportBoundsHoles,
                customViewport: exportMode ? exportViewport : initialViewport,
              },
              programmaticViewChangeRef
            );
          }
        }
      }, 100); // Small delay for panel transition
      
      return () => clearTimeout(timeoutId);
    }
    
    prevSelectedRef.current = selectedHoleId;
  }, [selectedHoleId, mapReady, data]);
  
  // Status color map
  const statusColorMap = {
    'Complete': '#10b981',
    'In Progress': '#ef4444',
    'Planned': '#6b7280',
    'Lost': '#000000'
  };
  
  // Get color for a hole based on filter type
  const getHoleColor = (hole, isSelected) => {
    // In-progress holes stay red in every view (zone / rig / progress override).
    if (hole.hole_status === 'In Progress') {
      return statusColorMap['In Progress'];
    }
    if (isSelected) return '#f9be0b'; // Gold for selected

    switch (filterType) {
      case 'zone':
        return zoneColorMap[hole.prospect] || '#6b7280';
      case 'rig':
        return rigColorMap[hole.drill_rig] || '#6b7280';
      case 'progress':
      case 'status':
      default:
        return statusColorMap[hole.hole_status] || '#6b7280';
    }
  };
  
  // Calculate drilled depth for a hole (use current_depth for In Progress, final_depth otherwise)
  const getHoleDrilledDepth = (hole) => {
    if (hole.hole_status === 'In Progress') {
      return parseFloat(hole.current_depth) || parseFloat(hole.final_depth) || 0;
    }
    return parseFloat(hole.final_depth) || 0;
  };
  
  // Calculate stats by grouping (excludes Planned holes)
  const calculateGroupStats = (groupField) => {
    const stats = {};
    const activeHoles = data?.filter(h => h.hole_status !== 'Planned') || [];
    
    activeHoles.forEach(hole => {
      const groupKey = hole[groupField] || 'Unknown';
      if (!stats[groupKey]) {
        stats[groupKey] = { count: 0, totalDepth: 0 };
      }
      stats[groupKey].count += 1;
      stats[groupKey].totalDepth += getHoleDrilledDepth(hole);
    });
    
    return stats;
  };
  
  // Pre-calculate stats for each filter type
  const statusStats = calculateGroupStats('hole_status');
  const zoneStats = calculateGroupStats('prospect');
  const rigStats = calculateGroupStats('drill_rig');

  // Watch for container resize (when panels are dragged)
  useEffect(() => {
    if (!mapRef.current || !mapInstanceRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize({ animate: false });
      }
    });
    
    resizeObserver.observe(mapRef.current);
    
    return () => resizeObserver.disconnect();
  }, [mapReady]);

  // Initialize map
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      const map = L.map(mapRef.current, {
        center: [62.5, -137.2],
        zoom: 10,
        zoomControl: !exportMode,
        attributionControl: !exportMode,
        zoomSnap: smoothZoom ? SMOOTH_ZOOM_SNAP : 1,
        zoomDelta: smoothZoom ? SMOOTH_ZOOM_SNAP : 1,
        wheelPxPerZoomLevel: smoothZoom ? 80 : 60,
      });

      const satelliteMap = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '© Esri',
        maxZoom: 18,
      });

      satelliteMap.addTo(map);

      if (!exportMode) {
        const baseLayers = {
          'Satellite': satelliteMap,
          'Street': L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap',
          }),
        };

        L.control.layers(baseLayers, {}, { position: 'topleft' }).addTo(map);
        L.control.scale({ position: 'bottomleft' }).addTo(map);
      }

      mapInstanceRef.current = map;
      setMapReady(true);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        setMapReady(false);
        exportReadyFiredRef.current = false;
        initialAutoFitDoneRef.current = false;
        userControlledViewportRef.current = false;
      }
    };
  }, [exportMode, smoothZoom]);

  // Export capture: invalidate size once mounted in fixed off-screen container
  useEffect(() => {
    if (!exportMode || !mapReady || !mapInstanceRef.current) return;

    const timeoutId = setTimeout(() => {
      mapInstanceRef.current?.invalidateSize({ animate: false });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [exportMode, mapReady, data]);

  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !onViewportChange || exportMode) return;

    const map = mapInstanceRef.current;
    const emitViewport = () => {
      if (programmaticViewChangeRef.current) {
        return;
      }
      userControlledViewportRef.current = true;
      const center = map.getCenter();
      onViewportChange({
        lat: center.lat,
        lon: center.lng,
        zoom: map.getZoom(),
      });
    };

    map.on('moveend', emitViewport);
    map.on('zoomend', emitViewport);

    return () => {
      map.off('moveend', emitViewport);
      map.off('zoomend', emitViewport);
    };
  }, [mapReady, onViewportChange, exportMode]);

  // Helper: Group holes by location (same pad)
  const groupHolesByLocation = (holes) => {
    const groups = [];
    const threshold = 0.00001; // ~1 meter tolerance for same location
    
    holes.forEach(hole => {
      const existingGroup = groups.find(group => 
        Math.abs(group.lat - hole.latitude) < threshold &&
        Math.abs(group.lon - hole.longitude) < threshold
      );
      
      if (existingGroup) {
        existingGroup.holes.push(hole);
      } else {
        groups.push({
          lat: hole.latitude,
          lon: hole.longitude,
          holes: [hole]
        });
      }
    });
    
    return groups;
  };

  // Add drill hole markers with smart positioning for overlapping holes
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !data) return;

    // Clear existing markers
    Object.values(markersRef.current).forEach(marker => marker.remove());
    markersRef.current = {};

    const validHoles = data.filter(hole => hole.latitude && hole.longitude);

    if (validHoles.length === 0) {
      if (exportMode && onExportReady && !exportReadyFiredRef.current) {
        exportReadyFiredRef.current = true;
        onExportReady();
      }
      return;
    }

    // Group holes by location
    const locationGroups = groupHolesByLocation(validHoles);
    const clusterFanRadius =
      filterType === 'progress' ? CLUSTER_FAN_RADIUS_PROGRESS : CLUSTER_FAN_RADIUS_DEFAULT;

    const createClassicIcon = (hole, isSelected, shortId) => {
      const safeId = escapeMarkerText(shortId);
      const color = getHoleColor(hole, isSelected);
      const isInProgress = hole.hole_status === 'In Progress';
      const isPulsing = isInProgress && !isSelected && !exportMode;
      let size;
      let fontSize;
      if (exportMode) {
        size = isInProgress ? EXPORT_MARKER_SIZE_IN_PROGRESS : EXPORT_MARKER_SIZE_DEFAULT;
        fontSize = isInProgress ? 10 : 8;
      } else {
        size = isSelected ? 32 : 24;
        fontSize = isSelected ? 14 : 11;
      }
      const rigLabel = inProgressRigLabelHtml(hole, Math.round(size / 2 + 20));

      return L.divIcon({
        className: 'custom-drill-marker drill-marker-mode-classic',
        html: `
        <div class="drill-marker-envelope drill-marker-hitbox"
          style="width:${CLASSIC_MARKER_ENVELOPE}px;height:${CLASSIC_MARKER_ENVELOPE}px;position:relative;transform:translate(-50%,-50%);">
          ${rigLabel}
          <div data-marker-scale-wrap
            class="drill-marker-scale-shell"
            style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%) scale(1);width:${size + 22}px;height:${size + 22}px;">
            <div style="position:relative;width:${size + 22}px;height:${size + 22}px;">
          ${isPulsing ? `<div class="pulse-ring" style="
            position: absolute;
            width: ${size}px;
            height: ${size}px;
            border-radius: 50%;
            border: 2px solid ${color};
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            animation: pulse-ring 2s ease-out infinite;
            pointer-events: none;
          "></div>` : ''}
          <div class="${isPulsing ? 'pulse-marker' : ''}" style="
            background-color: ${color};
            width: ${size}px;
            height: ${size}px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 ${isPulsing ? '4px 12px' : '2px 4px'} rgba(0,0,0,0.3);
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: ${fontSize}px;
            font-weight: 700;
            text-shadow: 0 1px 2px rgba(0,0,0,0.5);
          ">${safeId}</div>
            </div>
          </div>
        </div>`,
        iconSize: [CLASSIC_MARKER_ENVELOPE, CLASSIC_MARKER_ENVELOPE],
        iconAnchor: [CLASSIC_MARKER_ENVELOPE / 2, CLASSIC_MARKER_ENVELOPE / 2],
      });
    };

    const createProgressIcon = (hole, isSelected, shortId) => {
      const safeId = escapeMarkerText(shortId);
      const color = getHoleColor(hole, isSelected);
      const renderPx = Math.round(
        PROGRESS_MARKER_BASE_SIZE * (isSelected ? SELECTED_MARKER_SCALE_EXTRA : 1)
      );
      const pcts = parseHoleProgressPcts(hole);
      const { svg: ringsSvg, extent: ringCanvasPx } = buildProgressRingSvg(
        pcts,
        renderPx,
        progressRingVisibility
      );
      const innerPx = progressInnerHubPx(renderPx);
      const fontPx = Math.max(10, Math.round(renderPx * 0.2));
      const rigLabel = inProgressRigLabelHtml(hole, Math.round(ringCanvasPx / 2 + 6));

      return L.divIcon({
        className: 'custom-drill-marker drill-marker-mode-progress',
        html: `
        <div class="drill-marker-envelope drill-marker-hitbox progress-drill-hitbox"
          style="width:${PROGRESS_MARKER_ENVELOPE}px;height:${PROGRESS_MARKER_ENVELOPE}px;position:relative;transform:translate(-50%,-50%);">
          ${rigLabel}
          <div data-marker-scale-wrap class="drill-marker-scale-shell"
            style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%) scale(1);width:${ringCanvasPx}px;height:${ringCanvasPx}px;">
            <div style="position:relative;width:${ringCanvasPx}px;height:${ringCanvasPx}px;overflow:visible;">
              ${ringsSvg}
              <div style="
                position:absolute;
                top:50%;left:50%;
                transform:translate(-50%,-50%);
                width:${innerPx}px;height:${innerPx}px;
                border-radius:50%;
                background:${color};
                border:2px solid #fff;
                box-shadow:0 2px 6px rgba(0,0,0,0.35);
                display:flex;align-items:center;justify-content:center;
                color:#fff;font-size:${fontPx}px;font-weight:700;text-shadow:0 1px 2px rgba(0,0,0,0.55);
              ">${safeId}</div>
            </div>
          </div>
        </div>`,
        iconSize: [PROGRESS_MARKER_ENVELOPE, PROGRESS_MARKER_ENVELOPE],
        iconAnchor: [PROGRESS_MARKER_ENVELOPE / 2, PROGRESS_MARKER_ENVELOPE / 2],
      });
    };

    const createIcon = (hole, isSelected, shortId) =>
      filterType === 'progress'
        ? createProgressIcon(hole, isSelected, shortId)
        : createClassicIcon(hole, isSelected, shortId);

    const applyMarkerHoverScaled = (marker, hovered) => {
      const wrap = marker.getElement()?.querySelector('[data-marker-scale-wrap]');
      if (!wrap) return;
      const scale =
        hovered
          ? filterType === 'progress'
            ? HOVER_MARKER_SCALE
            : CLASSIC_HOVER_SCALE
          : 1;
      wrap.style.transform = `translate(-50%,-50%) scale(${scale})`;
    };

    // Add markers for each location group with smart positioning
    locationGroups.forEach(group => {
      const holesCount = group.holes.length;

      const sortedHoles = [...group.holes].sort((a, b) => {
        const priority = { 'In Progress': 3, 'Complete': 2, 'Planned': 1, 'Lost': 0 };
        return (priority[b.hole_status] || 0) - (priority[a.hole_status] || 0);
      });

      sortedHoles.forEach((hole, index) => {
        const isSelected = exportMode
          ? Boolean(highlightHoleId && hole.hole_id === highlightHoleId)
          : selectedHoleId === hole.hole_id;
        const shortId = formatMapHoleLabel(hole.hole_id);

        let lat = group.lat;
        let lon = group.lon;

        if (holesCount > 1) {
          const angle = (index / holesCount) * 2 * Math.PI;
          lat += clusterFanRadius * Math.cos(angle);
          lon += clusterFanRadius * Math.sin(angle);
        }

        const zIndex =
          hole.hole_status === 'In Progress' ? 1000 : hole.hole_status === 'Complete' ? 500 : 100;

        const marker = L.marker([lat, lon], {
          icon: createIcon(hole, isSelected, shortId),
          zIndexOffset: zIndex,
        });

        if (filterType === 'progress' && !exportMode) {
          marker.bindTooltip(holeProgressTooltipContent(hole, progressRingVisibility), {
            direction: 'top',
            sticky: true,
            opacity: 0.93,
            className: 'drill-hole-progress-tooltip',
          });
        }

        if (!exportMode) {
          marker.on('click', () => {
            if (onHoleClick) {
              onHoleClick(hole);
            }
          });

          marker.on('mouseover', () => {
            applyMarkerHoverScaled(marker, true);
            marker.setZIndexOffset(zIndex + 2500);
          });
          marker.on('mouseout', () => {
            applyMarkerHoverScaled(marker, false);
            marker.setZIndexOffset(zIndex);
          });
        }

        marker.addTo(mapInstanceRef.current);
        markersRef.current[hole.hole_id] = marker;
      });
    });

    // Fit map once for export capture, or once on load for interactive report zoom picker.
    if (validHoles.length > 0) {
      const shouldApplyView =
        exportMode ||
        (!userControlledViewportRef.current && !initialAutoFitDoneRef.current);

      if (shouldApplyView) {
        applyMapViewForHoles(
          mapInstanceRef.current,
          validHoles,
          {
            exportMode,
            focusSpanM,
            focusCenter,
            exportBoundsHoles,
            customViewport: exportMode ? exportViewport : initialViewport,
          },
          programmaticViewChangeRef
        );
        if (!exportMode) {
          initialAutoFitDoneRef.current = true;
        }
      }
    }

    if (exportMode && onExportReady && !exportReadyFiredRef.current) {
      exportReadyFiredRef.current = true;
      const timeoutId = setTimeout(() => onExportReady(), 150);
      return () => clearTimeout(timeoutId);
    }

  }, [data, mapReady, selectedHoleId, highlightHoleId, onHoleClick, filterType, zoneColorMap, rigColorMap, progressRingVisibility, exportMode, onExportReady, focusSpanM, focusCenter, exportBoundsHoles, exportViewport, initialViewport]);

  return (
    <div className="drill-hole-map-container">
      <div
        ref={mapRef}
        className={`drill-hole-map${exportMode ? ' drill-hole-map--export' : ''}`}
        style={{ height: '100%', width: '100%', borderRadius: exportMode ? 0 : '8px' }}
      />
      
      {showLegend && (
      <div className="map-legend" style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#f9fafb' }}>
          {filterType === 'status' && (<><SelkirkIcon name={ICON.drillFilterStatus} size={14} decorative /> Status</>)}
          {filterType === 'zone' && (<><SelkirkIcon name={ICON.drillFilterZone} size={14} decorative /> Zone</>)}
          {filterType === 'rig' && (<><SelkirkIcon name={ICON.drillFilterRig} size={14} decorative /> Drill Rig</>)}
          {filterType === 'progress' && (<><SelkirkIcon name={ICON.drillFilterProgress} size={14} decorative /> Progress rings</>)}
          {filterType === 'progress'
            ? (
              <span style={{ fontWeight: '400', color: '#9ca3af', fontSize: '0.68rem', marginLeft: '0.5rem' }}>
                (click each metric to hide/show its ring · center dot = hole status)
              </span>
            )
            : (
              <span style={{ fontWeight: '400', color: '#9ca3af', fontSize: '0.7rem', marginLeft: '0.5rem' }}>
                (click to filter)
              </span>
            )}
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.75rem' }}>
          {/* Status Legend */}
          {filterType === 'status' && [
            { key: 'Complete', color: '#10b981', label: 'Complete' },
            { key: 'In Progress', color: '#ef4444', label: 'In Progress' },
            { key: 'Planned', color: '#6b7280', label: 'Planned' },
            { key: 'Lost', color: '#000000', label: 'Lost' }
          ].map(({ key, color, label }) => {
            const stats = statusStats[key] || { count: 0, totalDepth: 0 };
            return (
              <div 
                key={key}
                onClick={() => onStatusToggle && onStatusToggle(key)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  opacity: statusFilters?.[key] ? 1 : 0.4,
                  background: statusFilters?.[key] ? 'rgba(249, 190, 11, 0.1)' : 'transparent'
                }}
              >
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  borderRadius: '50%', 
                  backgroundColor: color, 
                  border: '2px solid white',
                  opacity: statusFilters?.[key] ? 1 : 0.4,
                  flexShrink: 0
                }}></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    color: statusFilters?.[key] ? '#e5e7eb' : '#6b7280',
                    fontWeight: statusFilters?.[key] ? '500' : '400',
                    fontSize: '0.75rem'
                  }}>
                    {label}
                  </div>
                  {key !== 'Planned' && stats.count > 0 && (
                    <div style={{ 
                      fontSize: '0.65rem', 
                      color: '#9ca3af',
                      whiteSpace: 'nowrap'
                    }}>
                      {stats.count} holes • {stats.totalDepth.toLocaleString(undefined, {maximumFractionDigits: 0})}m
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* Zone Legend - sorted by total depth descending */}
          {filterType === 'zone' && Object.entries(zoneColorMap)
            .sort(([zoneA], [zoneB]) => {
              const statsA = zoneStats[zoneA] || { totalDepth: 0 };
              const statsB = zoneStats[zoneB] || { totalDepth: 0 };
              return statsB.totalDepth - statsA.totalDepth;
            })
            .map(([zone, color]) => {
              const stats = zoneStats[zone] || { count: 0, totalDepth: 0 };
              return (
                <div 
                  key={zone}
                  onClick={() => onZoneToggle && onZoneToggle(zone)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    cursor: 'pointer',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    opacity: zoneFilters?.[zone] ? 1 : 0.4,
                    background: zoneFilters?.[zone] ? 'rgba(249, 190, 11, 0.1)' : 'transparent'
                  }}
                >
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%', 
                    backgroundColor: color, 
                    border: '2px solid white',
                    opacity: zoneFilters?.[zone] ? 1 : 0.4,
                    flexShrink: 0
                  }}></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      color: zoneFilters?.[zone] ? '#e5e7eb' : '#6b7280',
                      fontWeight: zoneFilters?.[zone] ? '500' : '400',
                      fontSize: '0.75rem'
                    }}>
                      {zone}
                    </div>
                    {stats.count > 0 && (
                      <div style={{ 
                        fontSize: '0.65rem', 
                        color: '#9ca3af',
                        whiteSpace: 'nowrap'
                      }}>
                        {stats.count} holes • {stats.totalDepth.toLocaleString(undefined, {maximumFractionDigits: 0})}m
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          
          {/* Drill Rig Legend - sorted numerically */}
          {filterType === 'rig' && Object.entries(rigColorMap)
            .sort(([a], [b]) => {
              // Extract numbers from rig names for numeric sorting
              const numA = parseInt(a.replace(/\D/g, '')) || 0;
              const numB = parseInt(b.replace(/\D/g, '')) || 0;
              return numA - numB;
            })
            .map(([rig, color]) => {
              const stats = rigStats[rig] || { count: 0, totalDepth: 0 };
              return (
                <div 
                  key={rig}
                  onClick={() => onRigToggle && onRigToggle(rig)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    cursor: 'pointer',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    opacity: rigFilters?.[rig] ? 1 : 0.4,
                    background: rigFilters?.[rig] ? 'rgba(249, 190, 11, 0.1)' : 'transparent'
                  }}
                >
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%', 
                    backgroundColor: color, 
                    border: '2px solid white',
                    opacity: rigFilters?.[rig] ? 1 : 0.4,
                    flexShrink: 0
                  }}></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      color: rigFilters?.[rig] ? '#e5e7eb' : '#6b7280',
                      fontWeight: rigFilters?.[rig] ? '500' : '400',
                      fontSize: '0.75rem'
                    }}>
                      {rig}
                    </div>
                    {stats.count > 0 && (
                      <div style={{ 
                        fontSize: '0.65rem', 
                        color: '#9ca3af',
                        whiteSpace: 'nowrap'
                      }}>
                        {stats.count} holes • {stats.totalDepth.toLocaleString(undefined, {maximumFractionDigits: 0})}m
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          {filterType === 'progress' &&
            PROGRESS_RING_METRICS.map(({ label, color, field }) => {
              const on = ringVisible(field, progressRingVisibility);
              return (
                <div
                  key={field}
                  onClick={() => onProgressMetricToggle && onProgressMetricToggle(field)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: on ? 1 : 0.38,
                    background: on ? 'rgba(249, 190, 11, 0.1)' : 'transparent',
                  }}
                >
                  <div
                    style={{
                      width: '13px',
                      height: '13px',
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: color,
                      border: '2px solid #e5e7eb',
                      opacity: on ? 1 : 0.45,
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        color: on ? '#e5e7eb' : '#6b7280',
                        fontWeight: on ? '500' : '400',
                        fontSize: '0.75rem',
                      }}
                    >
                      {label}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      )}
    </div>
  );
};

export default DrillHoleMap;

